import { useMemo } from "react";
import { useGetDoclist, usePopulateBulkDoclistPages } from "~/api/chat/chat";
import { DoclistTreeItemSectionsItem, Page, PresignedUrls } from "~/api/model";
import { useChatDocumentParams } from "~/helpers";

export type PageItem = {
  pageId: string;
  sectionId: string;
  opensSection?: {
    sectionName: string;
  };
  getUrls: () => Promise<PresignedUrls>;
  rotate: number;
  width: number;
  height: number;
  batesNumber?: string | null;
};

export const useDocList = () => {
  const { mutateAsync: populatePages } = usePopulateBulkDoclistPages();

  const { organizationId, caseId, chatId, documentId } =
    useChatDocumentParams();
  const {
    data: docList,
    isLoading: isDocListLoading,
    isError: isDocListError,
  } = useGetDoclist(
    organizationId,
    caseId,
    documentId ? { documentId } : undefined,
    {
      query: {
        staleTime: 5 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
      },
    }
  );

  const sections = useMemo(() => {
    return docList?.tree.flatMap((doc) => doc.sections) || [];
  }, [docList]);

  const absolutePageNumbers = useMemo(() => {
    const pageNumbers: Record<string, number> = {};
    let absolutePageIndex = 1;

    sections.forEach((section) => {
      section.pages?.forEach((page) => {
        if (page.id) {
          pageNumbers[page.id] = absolutePageIndex;
          absolutePageIndex++;
        }
      });
    });

    return pageNumbers;
  }, [sections]);

  const pageItems = useMemo<PageItem[]>(() => {
    const getPageItemsFromSections = (
      sections: DoclistTreeItemSectionsItem[],
      populatePage: (
        pageId: string,
        sectionPageId: string
      ) => Promise<PresignedUrls>
    ): PageItem[] => {
      const pageItems = sections.flatMap(
        (section: DoclistTreeItemSectionsItem) =>
          (section.pages || []).map((page: Page, index: number) => {
            const pageItem: PageItem = {
              getUrls: () => populatePage(page.id, page.sectionPageId ?? ""),
              rotate: page.rotate ?? 0,
              pageId: page.id,
              sectionId: section.id,
              width: page.width ?? 0,
              height: page.height ?? 0,
              batesNumber: page.batesNumber,
            };
            if (index === 0) {
              pageItem.opensSection = {
                sectionName: section.name,
              };
            }
            return pageItem;
          })
      );

      return pageItems;
    };
    const populatePage = async (
      pageId: string,
      sectionPageId: string
    ): Promise<PresignedUrls> => {
      const result = await populatePages({
        organizationId,
        projectId: caseId,
        chatId,
        data: { sectionPageIds: [sectionPageId] },
      });

      return result[pageId];
    };

    return getPageItemsFromSections(sections, populatePage);
  }, [caseId, chatId, organizationId, populatePages, sections]);

  const firstPageIds = useMemo<Record<string, string>>(() => {
    return sections.reduce(
      (acc, section) => {
        if (section.pageIds && section.pageIds.length > 0) {
          acc[section.id] = section.pageIds[0];
        }
        return acc;
      },
      {} as Record<string, string>
    );
  }, [sections]);

  return {
    docList,
    isDocListLoading,
    isDocListError,
    pageItems,
    absolutePageNumbers,
    firstPageIds,
  };
};
